/**
 * Static mirror of iOS `SupportedCurrency` — cosmetic template metadata only.
 * Source of truth for which currencies the app can pick remains on-device.
 * Never treat this as a live currency catalog or ledger setting.
 */
export const SUPPORTED_CURRENCY_CODES = [
  "PHP",
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "SGD",
  "HKD",
  "AED",
  "AUD",
  "CAD",
  "CHF",
  "CNY",
  "KRW",
  "THB",
  "MYR",
  "IDR",
  "INR",
  "NZD",
  "TWD",
] as const;

export type SupportedCurrencyCode = (typeof SUPPORTED_CURRENCY_CODES)[number];

const SUPPORTED = new Set<string>(SUPPORTED_CURRENCY_CODES);

export function isSupportedCurrencyCode(code: string | null | undefined): code is SupportedCurrencyCode {
  return Boolean(code && SUPPORTED.has(code.toUpperCase()));
}

export function normalizeCurrencyCode(code: string | null | undefined): string | null {
  if (!code) return null;
  const upper = code.trim().toUpperCase();
  return SUPPORTED.has(upper) ? upper : null;
}

export function parseAllowedCurrencyCodesJSON(raw: string | null | undefined): string[] {
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const codes = parsed
      .map((value) => (typeof value === "string" ? normalizeCurrencyCode(value) : null))
      .filter((value): value is string => Boolean(value));
    return Array.from(new Set(codes));
  } catch {
    return [];
  }
}

export function encodeAllowedCurrencyCodes(codes: string[]): string {
  const cleaned = Array.from(
    new Set(codes.map((code) => normalizeCurrencyCode(code)).filter((code): code is string => Boolean(code))),
  );
  return JSON.stringify(cleaned);
}

/** Foreign savings selectable set: every supported code except PHP. */
export function foreignSelectableCurrencyCodes(): string[] {
  return SUPPORTED_CURRENCY_CODES.filter((code) => code !== "PHP");
}
